import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { toast } from 'sonner'
import { VISIBLE_INTEGRATIONS, INTEGRATIONS } from '@super/shared-types'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import type { IntegrationStatus } from '@/lib/api/integrations/integrations.client'
import { IntegrationCard } from './IntegrationCard'
import { Input } from '@/components/ui/input'
import { Loader } from '@/components/ui/loader'
import { useAuth } from '@/hooks/use-auth'
import { useAppAuth } from '@/contexts/app-auth.context'
import { dispatchCatalogClick } from './vendors'

type IntegrationDef = (typeof INTEGRATIONS)[keyof typeof INTEGRATIONS]

export interface IntegrationCatalogProps {
  variant: 'onboarding' | 'settings'
}

function sortIntegrations(defs: IntegrationDef[]): IntegrationDef[] {
  return [...defs].sort((a, b) => {
    const orderA =
      'FEATURED_ORDER' in a && typeof a.FEATURED_ORDER === 'number'
        ? a.FEATURED_ORDER
        : Number.POSITIVE_INFINITY
    const orderB =
      'FEATURED_ORDER' in b && typeof b.FEATURED_ORDER === 'number'
        ? b.FEATURED_ORDER
        : Number.POSITIVE_INFINITY
    if (orderA !== orderB) return orderA - orderB
    return a.NAME.localeCompare(b.NAME)
  })
}

function matchesQuery(def: IntegrationDef, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    def.NAME.toLowerCase().includes(q) ||
    def.TYPE.toLowerCase().includes(q) ||
    ('DESCRIPTION' in def &&
      typeof def.DESCRIPTION === 'string' &&
      def.DESCRIPTION.toLowerCase().includes(q))
  )
}

export function IntegrationCatalog({ variant }: IntegrationCatalogProps) {
  const navigate = useNavigate()
  const { isSuperAdmin } = useAuth()
  const { currentCompany } = useAppAuth()
  const [statuses, setStatuses] = useState<IntegrationStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        const result = await integrationsClient.getAllIntegrationsStatus()
        if (!cancelled) setStatuses(result)
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load integration statuses', error)
          toast.error('Failed to load integrations')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const statusByType = useMemo(() => {
    const map = new Map<string, IntegrationStatus>()
    for (const status of statuses) map.set(status.type, status)
    return map
  }, [statuses])

  const sortedIntegrations = useMemo(
    () => sortIntegrations(VISIBLE_INTEGRATIONS),
    [],
  )

  const filteredIntegrations = useMemo(
    () => sortedIntegrations.filter(def => matchesQuery(def, query)),
    [sortedIntegrations, query],
  )

  // Card-click delegates to the vendor's catalog handler. OAuth vendors fire
  // OAuth immediately (no sub-page detour); form vendors navigate to the
  // sub-page. Both Settings and Onboarding dispatch through the same map.
  const handleConnectClick = async (integration: IntegrationDef) => {
    try {
      await dispatchCatalogClick(integration.TYPE, {
        mode: variant,
        navigate,
        isSuperAdmin,
        currentCompanyId: currentCompany?.id ?? null,
      })
    } catch (error) {
      console.error(`Error connecting ${integration.NAME}`, error)
      toast.error(`Couldn't start ${integration.NAME} connect.`)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader text="Loading integrations…" />
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={
            variant === 'onboarding'
              ? 'Search integrations…'
              : `Search ${sortedIntegrations.length} integrations…`
          }
          className="pl-9"
        />
      </div>

      {filteredIntegrations.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          No integrations match "{query}".
        </p>
      ) : (
        filteredIntegrations.map(integration => (
          <IntegrationCard
            key={integration.TYPE}
            integration={integration}
            status={statusByType.get(integration.TYPE)}
            onConnect={() => handleConnectClick(integration)}
          />
        ))
      )}
    </div>
  )
}

export function useIntegrationConnectedCount(): {
  count: number
  loading: boolean
} {
  const [statuses, setStatuses] = useState<IntegrationStatus[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const result = await integrationsClient.getAllIntegrationsStatus()
        if (!cancelled) setStatuses(result)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  return {
    count: statuses.filter(s => s.connected).length,
    loading,
  }
}
